import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useState, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { showEcomModal, showMessageModal } from '../../actions/event';
import {
  mapVehicleData,
  track,
  trackConversionEvent,
  trackEcomEvent,
} from '../../actions/tracking';
import { parsers } from '../../helpers';
import { getItem, getItemAuctionUrl, getItemCommerce } from '../../selectors/item';
import {
  getItemRetailerContactOptions,
  getItemRetailerPhoneParams,
} from '../../selectors/item-retailer';
import { getRetailerId, isRetailerUsingFreespee } from '../../selectors/retailer';
import { EcomEvents, conversionEvents, eventTypes } from '../../shared/tracking';
import { phonenumbers } from '../../utils';
const EcomButton = lazy(() => import('../../components/ecom-button-v2/EcomButtonV2'));
import { RenderClientSide } from '../../components/render';

const FloatingAction = () => {
  const { t } = useTranslation(['global', 'item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const commerce = useSelector(getItemCommerce);
  const contactOptions = useSelector(getItemRetailerContactOptions);
  const auctionUrl = useSelector(getItemAuctionUrl);
  const retailerId = useSelector(getRetailerId);
  const phoneParams = useSelector(getItemRetailerPhoneParams);
  const usingPhoneNumberGeneration = useSelector(isRetailerUsingFreespee);
  const actions = bindActionCreators(
    {
      showMessageModal,
      trackConversionEvent,
      showEcomModal,
      trackEcomEvent,
      track,
    },
    dispatch,
  );

  const [email, setEmail] = useState<string | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();

  const handleEcomButtonClick = () => {
    actions.trackEcomEvent(EcomEvents.ECOM_BUTTON_CLICK, null, item);
    actions.showEcomModal();
  };

  const shouldShowEcomButton = commerce?.enabled && !commerce.disabledOnWayke;
  const objectIsReserved = shouldShowEcomButton && commerce.reserved;

  const handleEmailClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const elem = e.currentTarget;
    if (elem.getAttribute('href') === '#') {
      const subject = encodeURIComponent(
        t('sendEmailSubject', {
          ns: 'item',
          brandName: t('brandName', { ns: 'global' }),
          item: `${item?.registrationNumber}, ${item?.manufacturer} ${item?.modelSeries}`,
        }),
      );
      elem.href = `mailto:${contactOptions?.email}?subject=${subject}`;
      elem.click();
      e.preventDefault();
      setEmail(`mailto:${contactOptions?.email}?subject=${subject}`);
    } else {
      actions.track(eventTypes.CONVERSION, {
        item: mapVehicleData(item, true),
        conversionType: conversionEvents.CONVERSION_MAIL_SENT,
      });
    }
  };

  const handlePhoneClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const elem = e.currentTarget;
    if (elem.getAttribute('href') === '#') {
      e.preventDefault();
      if (usingPhoneNumberGeneration) {
        phonenumbers.convert(
          contactOptions?.telephone?.international || '',
          phoneParams!,
          (generated) => {
            const parsed = parsers.parsePhoneNumber(generated);
            elem.href = parsed.e164 || '';
            elem.click();
            setPhoneNumber(parsed.e164);
          },
        );
      } else {
        elem.href = contactOptions?.telephone?.e164 || '';
        elem.click();
        setPhoneNumber(contactOptions?.telephone?.e164);
      }
    } else {
      actions.track(eventTypes.CONVERSION, {
        item: mapVehicleData(item, true),
        conversionType: conversionEvents.CONVERSION_PHONENUMBER_CALL,
      });
    }
  };

  return (
    <div data-wx-floatingaction="" className="is-visible">
      {auctionUrl ? (
        <a
          className="floating-action"
          href={auctionUrl}
          onClick={handleEcomButtonClick}
          title={t('To auction', { ns: 'item' })}
        >
          <div className="floating-action-icon">
            <i className="icon-auction no-margin" />
          </div>
          <div className="floating-action-label">{t('To auction', { ns: 'item' })}</div>
        </a>
      ) : (
        <>
          {shouldShowEcomButton && item?.id && (
            <RenderClientSide
              skeleton={
                <button
                  type="button"
                  className="floating-action is-primary"
                  disabled={objectIsReserved}
                >
                  <div className="floating-action-icon">
                    <i className="icon-cart no-margin" />
                  </div>
                  <div className="floating-action-label">{t('Buy online', { ns: 'item' })}</div>
                </button>
              }
            >
              <EcomButton id={item.id} disabled={objectIsReserved} floatingAction />
            </RenderClientSide>
          )}
          {retailerId && (
            <button
              type="button"
              className="floating-action"
              onClick={actions.showMessageModal}
              title={t('sendMessageShort', { ns: 'item' })}
            >
              <div className="floating-action-icon">
                <i className="icon-message no-margin" />
              </div>
              <div className="floating-action-label">{t('sendMessageShort', { ns: 'item' })}</div>
            </button>
          )}
          {contactOptions?.telephone?.e164 && (
            <a
              href={phoneNumber ? phoneNumber : '#'}
              className="floating-action is-light"
              onClick={handlePhoneClick}
              title={t('callShort', { ns: 'item' })}
            >
              <div className="floating-action-icon">
                <i className="icon-phone no-margin" />
              </div>
              <div className="floating-action-label">{t('callShort', { ns: 'item' })}</div>
            </a>
          )}
          {contactOptions?.email && (
            <a
              href={email ? email : '#'}
              onClick={handleEmailClick}
              title={t('emailShort', { ns: 'item' })}
              className="floating-action is-light"
            >
              <div className="floating-action-icon">
                <i className="icon-email no-margin" />
              </div>
              <div className="floating-action-label">{t('emailShort', { ns: 'item' })}</div>
            </a>
          )}
        </>
      )}
    </div>
  );
};

export default FloatingAction;
